import { QUEUE_SHELL, QUEUE_SCROLL, QUEUE_TABLE, QUEUE_THEAD, QUEUE_TH, QUEUE_TD, QUEUE_EMPTY } from './queueTableStyles';
import React, { useState } from 'react';
import { fmtDate } from '../../lib/dates';

// The "Awaiting review" queue — debriefs somebody has submitted for approval.
//
// ADMIN ONLY. Briefs.js decides that and simply doesn't render the tab for
// anyone else, so there is no permission check in here to get wrong.
//
// AGE IS IN WORKING DAYS, not calendar days (reviewQueue does the counting).
// A debrief submitted on Friday afternoon is not two days late on Sunday, and
// a queue that says it is trains people to ignore the number.
//
// NOTHING AUTO-RESOLVES. No auto-approve after N days, no auto-expiry. An
// unread debrief stays unread and keeps counting — the point of the queue is
// that a person looked at it, and a timer that clears the row would quietly
// publish work nobody read.

const FONT = "font-['Metropolis']";

export default function DebriefReviewQueue({ rows, onOpen, onApprove, onRequestChanges, currentUserId }) {
  // Which row is mid-request. Keyed by id rather than a single boolean so two
  // quick clicks on different rows can't disable each other's buttons.
  const [busy, setBusy] = useState(null);

  if (!rows || rows.length === 0) {
    return (
      <div className="mx-8 mt-8">
        <div className={`${FONT} ${QUEUE_EMPTY}`}>
          <div className="text-[14px] font-bold text-ink mb-1">No debriefs waiting for review</div>
          <p className="text-[12px] text-[#51534A]">When someone submits a debrief for approval it appears here — oldest first. Nothing publishes until an admin approves it.</p>
        </div>
      </div>
    );
  }

  const staleCount = rows.filter(r => r.stale).length;

  const run = async (id, fn) => {
    setBusy(id);
    try { await fn(); } finally { setBusy(null); }
  };

  return (
    <div className="mx-8 mt-6">
      <p className={`${FONT} text-[12px] text-[#51534A] mb-3`}>
        {rows.length} debrief{rows.length === 1 ? '' : 's'} waiting for approval — oldest first.
        {staleCount > 0 && <span className="text-[#B47509] font-semibold"> {staleCount} {staleCount === 1 ? 'has' : 'have'} been waiting over a working week.</span>}
      </p>
      <div className={`${QUEUE_SHELL} ${QUEUE_SCROLL}`}>
        <table className={`${QUEUE_TABLE} min-w-[820px]`}>
          <thead className={QUEUE_THEAD}>
            <tr>
              <th scope="col" className={QUEUE_TH}>Project</th>
              <th scope="col" className={QUEUE_TH}>Client</th>
              <th scope="col" className={QUEUE_TH}>Written by</th>
              <th scope="col" className={QUEUE_TH}>Submitted</th>
              <th scope="col" className={`${QUEUE_TH} text-right`} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              // Approving your own debrief is allowed — with two people on a
              // rota, forbidding it would deadlock the queue — but it is
              // recorded as self_approved in the history and named here, so
              // it's a visible choice rather than a silent one.
              const ownWork = d.written_by && d.written_by === currentUserId;
              const author = d.written_by_user?.full_name || 'Unknown';
              return (
                <tr key={d.id} className={`${FONT} border-b border-line last:border-0 hover:bg-[#F7F9F0]`}>
                  <td className={QUEUE_TD}>
                    <button type="button" onClick={() => onOpen?.(d)}
                      className="text-[13px] font-semibold text-ink hover:text-fusion-forest text-left leading-snug">
                      {d.project_name || 'Untitled project'}
                    </button>
                    {d.project_ref && <div className="text-[11px] text-[#9BA08C] mt-0.5">{d.project_ref}</div>}
                  </td>
                  <td className="px-4 py-3 align-top text-[12.5px] text-fusion-mid-grey">{d.client || '—'}</td>
                  <td className="px-4 py-3 align-top text-[12.5px] text-fusion-mid-grey">
                    {author}
                    {ownWork && <div className="text-[11px] text-[#9BA08C]">yours</div>}
                  </td>
                  <td className={QUEUE_TD}>
                    <div className="text-[12.5px] text-ink">{fmtDate(d.submitted_at, '—')}</div>
                    <div className={`text-[11px] ${d.stale ? 'text-[#B47509] font-semibold' : 'text-[#9BA08C]'}`}>
                      {d.ageWorkingDays === 0 ? 'today'
                        : `${d.ageWorkingDays} working day${d.ageWorkingDays === 1 ? '' : 's'} ago`}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                    {/* Request changes sits FIRST and quiet, approve second and
                        solid: publishing is the irreversible-feeling one, so it
                        shouldn't be the button under the cursor by default. */}
                    <button type="button" disabled={busy === d.id}
                      onClick={() => run(d.id, () => onRequestChanges?.(d))}
                      className="text-[11px] font-bold rounded-full px-3 py-1.5 border border-line text-fusion-mid-grey hover:border-[#B47509] hover:text-[#B47509] mr-2 disabled:opacity-50">
                      Request changes
                    </button>
                    <button type="button" disabled={busy === d.id}
                      onClick={() => run(d.id, () => onApprove?.(d))}
                      className="text-[11px] font-bold rounded-full px-3.5 py-1.5 text-white bg-fusion-forest hover:brightness-110 transition-[filter] disabled:opacity-50">
                      {ownWork ? 'Approve (yours)' : 'Approve'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={`${FONT} text-[11px] text-[#9BA08C] mt-3`}>
        Approving publishes the debrief and notifies the author and every action owner who is a Supply Pilot user. Requesting changes sends it back to draft with your note.
      </p>
    </div>
  );
}
